import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useLocation } from "react-router-dom";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import { isDeveloper } from "../utils/developer";
import FeatureUnavailable from "./FeatureUnavailable";

export default function OperationalModeGuard({ session, profile, children }) {
  const location = useLocation();
  const { ready, isEnabled } = useFeatureFlags();
  const developer = isDeveloper(session);
  const position = String(profile?.position || "");
  const isManagement = position === "Management";
  const isStaff = isManagement || position === "Staff";
  const path = location.pathname;
  const [serviceStatus, setServiceStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const { data } = await supabase.from("developer_site_settings").select("value").eq("key", "service_status").maybeSingle();
      if (alive) setServiceStatus(data?.value || null);
    };
    void load();
    const channel = supabase.channel("ruht-service-status").on("postgres_changes", { event: "*", schema: "public", table: "developer_site_settings", filter: "key=eq.service_status" }, load).subscribe();
    return () => { alive = false; supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!ready || developer || isEnabled("feature.site_read_only")) return undefined;
    const stopSubmit = (event) => {
      const form = event.target;
      if (form?.closest?.(".pguh-developer-controls") || form?.dataset?.readonlyAllow === "true") return;
      event.preventDefault();
      event.stopPropagation();
      window.dispatchEvent(new CustomEvent("ruht:readonly-write-blocked"));
    };
    document.addEventListener("submit", stopSubmit, true);
    return () => document.removeEventListener("submit", stopSubmit, true);
  }, [developer, isEnabled, ready]);

  if (!ready || developer) return children;
  if (!isEnabled("feature.emergency_management_only") && !isManagement) return <FeatureUnavailable session={session} profile={profile} intranet={path.startsWith("/intranet")} management={false} />;
  if (!isEnabled("feature.emergency_staff_only") && !isStaff) return <FeatureUnavailable session={session} profile={profile} intranet={path.startsWith("/intranet")} management={false} />;
  if (!isEnabled("feature.emergency_hide_public_facing") && !path.startsWith("/intranet") && !path.startsWith("/login") && !path.startsWith("/maintenance")) return <FeatureUnavailable session={session} profile={profile} intranet={false} management={false} />;
  if (!isEnabled("feature.emergency_hide_patient_facing") && path.startsWith("/patient-portal")) return <FeatureUnavailable session={session} profile={profile} intranet={false} management={false} />;
  if (!isEnabled("feature.ecamis_patient_portal") && path.startsWith("/patient-portal")) return <FeatureUnavailable session={session} profile={profile} intranet={false} management={false} />;
  if (!isEnabled("feature.patient_admissions") && path.startsWith("/intranet/ecamis/create")) return <FeatureUnavailable session={session} profile={profile} intranet management={false} />;
  if (!isEnabled("feature.ecamis_shift_calendar") && (path.startsWith("/intranet/calendar") || path.startsWith("/intranet/ecamis/shift"))) return <FeatureUnavailable session={session} profile={profile} intranet management={false} />;

  return (
    <>
      {serviceStatus && serviceStatus.status && serviceStatus.status !== "operational" && <div className={`pguh-global-service-status is-${serviceStatus.status}`} role="status"><strong>{String(serviceStatus.status).replace(/_/g, " ")}</strong>{serviceStatus.message ? ` — ${serviceStatus.message}` : ""}</div>}
      {!isEnabled("feature.site_read_only") && <div className="pguh-global-readonly" role="status"><strong>Read-only mode:</strong> viewing remains available, but normal form submissions are temporarily disabled.</div>}
      {children}
    </>
  );
}
